import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Badge,
  Box,
  ButtonBase,
  Chip,
  CircularProgress,
  Dialog,
  Divider,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@material-ui/core";
import Button from "../TabletButton";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import AddPhotoAlternateOutlinedIcon from "@material-ui/icons/AddPhotoAlternateOutlined";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import BugReportOutlinedIcon from "@material-ui/icons/BugReportOutlined";
import CloseIcon from "@material-ui/icons/Close";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import ForumOutlinedIcon from "@material-ui/icons/ForumOutlined";
import ImageOutlinedIcon from "@material-ui/icons/ImageOutlined";
import RefreshIcon from "@material-ui/icons/Refresh";
import SearchIcon from "@material-ui/icons/Search";
import SendOutlinedIcon from "@material-ui/icons/SendOutlined";
import { toast } from "react-toastify";

import ButtonWithSpinner from "../ButtonWithSpinner";
import useSupportIssues from "../../hooks/useSupportIssues";
import {
  getSupportAttachment,
  getSupportErrorMessage,
  isAmbiguousSupportRequestError,
} from "../../services/supportIssues";

const MAX_IMAGES = 5;
const MAX_IMAGE_BYTES = 8 * 1024 * 1024;
const ACCEPTED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"];

export const SUPPORT_STATUS = {
  open: { label: "Aberto", palette: "info" },
  in_review: { label: "Em análise", palette: "secondary" },
  awaiting_user: { label: "Aguardando usuário", palette: "warning" },
  resolved: { label: "Corrigido", palette: "success" },
  closed: { label: "Finalizado", palette: "text" },
};

const useStyles = makeStyles((theme) => ({
  dialogPaper: {
    height: "min(88vh, 820px)",
    borderRadius: 8,
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    backgroundColor: theme.palette.background.paper,
    [theme.breakpoints.down("sm")]: {
      height: "100%",
      maxHeight: "100%",
      borderRadius: 0,
      margin: 0,
    },
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    padding: theme.spacing(2.5, 3, 1.5),
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(1.5, 2, 1),
    },
  },
  headerIcon: {
    ...theme.neoUI?.buttons?.action?.root,
    width: 40,
    minWidth: 40,
    height: 40,
    borderRadius: theme.shape.borderRadius,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 0,
  },
  headerCopy: {
    flex: 1,
    minWidth: 0,
  },
  headerTitle: {
    fontSize: 18,
    lineHeight: 1.25,
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
  headerSubtitle: {
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.25),
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    [theme.breakpoints.down("xs")]: {
      display: "none",
    },
  },
  closeButton: {
    ...theme.neoUI?.buttons?.action?.cancel?.root,
    height: 32,
    flexShrink: 0,
  },
  issueNav: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(0, 3, 2),
    overflowX: "auto",
    borderBottom: `1px solid ${theme.palette.divider}`,
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(0, 2, 1.5),
    },
  },
  issueNavButton: {
    textTransform: "none",
    fontWeight: 600,
    fontSize: "0.875rem",
    minHeight: 32,
    height: 32,
    minWidth: 0,
    padding: theme.spacing(0, 1.5),
    borderRadius: 8,
    border: `1px solid ${theme.palette.divider}`,
    color: theme.palette.text.primary,
    backgroundColor: "transparent",
    flexShrink: 0,
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
      borderColor: theme.palette.primary.main,
    },
    "@media (pointer: coarse)": {
      minHeight: 44,
      height: 44,
    },
  },
  issueNavButtonSelected: {
    ...theme.neoUI?.buttons?.action?.root,
    fontWeight: 600,
    minHeight: 32,
    height: 32,
    flexShrink: 0,
  },
  body: {
    flex: 1,
    minHeight: 0,
    display: "flex",
    overflow: "hidden",
  },
  createPane: {
    width: "100%",
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    overflowY: "auto",
    padding: theme.spacing(3),
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(2),
    },
  },
  createForm: {
    width: "100%",
    minWidth: 0,
    flex: "1 0 auto",
    display: "flex",
    flexDirection: "column",
    "& > *": {
      flexShrink: 0,
    },
  },
  createDescription: {
    flex: "1 0 208px",
    minHeight: 208,
    "& .MuiOutlinedInput-root": {
      flex: 1,
      alignItems: "stretch",
    },
    "& textarea": {
      height: "auto",
      resize: "none",
      overflowY: "auto",
    },
  },
  intro: {
    marginBottom: theme.spacing(2.5),
  },
  introTitle: {
    fontSize: 20,
    fontWeight: 600,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(0.5),
  },
  secondaryText: {
    color: theme.palette.text.secondary,
  },
  field: {
    marginBottom: theme.spacing(2),
  },
  counter: {
    textAlign: "right",
    color: theme.palette.text.secondary,
    fontSize: 12,
    marginTop: theme.spacing(-1.25),
    marginBottom: theme.spacing(1.5),
  },
  uploadRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    flexWrap: "wrap",
  },
  uploadButton: {
    textTransform: "none",
    borderRadius: 8,
    fontWeight: 600,
  },
  previewGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(112px, 1fr))",
    gap: theme.spacing(1.5),
    marginTop: theme.spacing(2),
  },
  previewItem: {
    position: "relative",
    minWidth: 0,
  },
  previewImage: {
    width: "100%",
    height: 84,
    objectFit: "cover",
    display: "block",
    borderRadius: 10,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.action.hover,
  },
  previewName: {
    display: "block",
    marginTop: theme.spacing(0.5),
    color: theme.palette.text.secondary,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    fontSize: 12,
  },
  removePreview: {
    position: "absolute",
    top: 4,
    right: 4,
    width: 28,
    height: 28,
    color: "#fff",
    backgroundColor: "rgba(20, 20, 20, 0.72)",
    "&:hover": {
      backgroundColor: "rgba(20, 20, 20, 0.9)",
    },
  },
  formActions: {
    display: "flex",
    justifyContent: "flex-end",
    marginTop: theme.spacing(3),
  },
  primaryButton: {
    ...theme.neoUI?.buttons?.action?.root,
    minWidth: 150,
  },
  errorBox: {
    padding: theme.spacing(1.25, 1.5),
    borderRadius: 8,
    color: theme.palette.error.main,
    backgroundColor: theme.palette.error.main + "12",
    marginTop: theme.spacing(1.5),
  },
  browserPane: {
    width: "100%",
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
  },
  listPane: {
    flex: 1,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    height: "100%",
  },
  filters: {
    padding: theme.spacing(2),
    display: "grid",
    gridTemplateColumns: "auto minmax(220px, 1fr) minmax(145px, 180px)",
    alignItems: "start",
    gap: theme.spacing(1),
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    },
  },
  filtersSuper: {
    gridTemplateColumns: "auto minmax(220px, 1fr) minmax(145px, 180px) minmax(155px, 210px)",
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    },
  },
  newIssueButton: {
    ...theme.neoUI?.buttons?.action?.root,
    height: 40,
    minHeight: 40,
    whiteSpace: "nowrap",
    textTransform: "none",
    backgroundColor: theme.palette.primary.main,
    color: "#fff",
    [theme.breakpoints.down("xs")]: {
      gridColumn: "1 / -1",
    },
  },
  searchField: {
    minWidth: 0,
    [theme.breakpoints.down("xs")]: {
      gridColumn: "1 / -1",
    },
  },
  filterFields: {
    display: "contents",
  },
  listScroll: {
    flex: 1,
    minHeight: 0,
    overflowY: "auto",
  },
  listFeedback: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(1, 2),
    color: theme.palette.text.secondary,
    backgroundColor: theme.palette.action.hover,
  },
  listFeedbackError: {
    color: theme.palette.error.dark || theme.palette.error.main,
  },
  listFeedbackAction: {
    marginLeft: "auto",
    flexShrink: 0,
    textTransform: "none",
  },
  issueRow: {
    width: "100%",
    display: "block",
    textAlign: "left",
    padding: theme.spacing(1.75, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    color: theme.palette.text.primary,
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
    "&:focus-visible": {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: -2,
    },
  },
  issueRowSelected: {
    backgroundColor: theme.palette.action.selected,
  },
  issueRowTop: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(0.75),
  },
  issueCode: {
    fontSize: 12,
    fontWeight: 700,
    color: theme.palette.primary.main,
  },
  issueSubject: {
    fontWeight: 600,
    lineHeight: 1.35,
    overflowWrap: "anywhere",
  },
  issueMeta: {
    marginTop: theme.spacing(0.75),
    color: theme.palette.text.secondary,
    display: "flex",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    fontSize: 12,
  },
  unreadDot: {
    width: 8,
    height: 8,
    borderRadius: "50%",
    backgroundColor: theme.palette.primary.main,
    marginLeft: "auto",
    flexShrink: 0,
  },
  statePane: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    padding: theme.spacing(4),
    color: theme.palette.text.secondary,
  },
  stateIcon: {
    fontSize: 42,
    color: theme.palette.action.disabled,
    marginBottom: theme.spacing(1.5),
  },
  loadMore: {
    margin: theme.spacing(1.5, 2),
    textTransform: "none",
  },
  detailPane: {
    width: "100%",
    minWidth: 0,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    height: "100%",
  },
  detailHeader: {
    padding: theme.spacing(1.75, 2),
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.25),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  mobileBack: {
    display: "none",
    [theme.breakpoints.down("sm")]: {
      display: "inline-flex",
    },
  },
  detailHeading: {
    flex: 1,
    minWidth: 0,
  },
  detailTitle: {
    fontWeight: 600,
    color: theme.palette.text.primary,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  history: {
    flex: 1,
    minHeight: 0,
    overflowY: "auto",
    padding: theme.spacing(2, 2.5),
  },
  message: {
    padding: theme.spacing(1.5, 0),
  },
  messageHeader: {
    display: "flex",
    alignItems: "baseline",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(0.75),
  },
  actor: {
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
  timestamp: {
    fontSize: 12,
    color: theme.palette.text.secondary,
  },
  messageBody: {
    maxWidth: "72ch",
    color: theme.palette.text.primary,
    whiteSpace: "pre-wrap",
    overflowWrap: "anywhere",
    lineHeight: 1.55,
  },
  statusActivity: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    color: theme.palette.text.secondary,
    fontSize: 13,
    flexWrap: "wrap",
  },
  attachments: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(104px, 150px))",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1.25),
  },
  attachmentButton: {
    width: "100%",
    display: "block",
    textAlign: "left",
    borderRadius: 8,
    overflow: "hidden",
    border: `1px solid ${theme.palette.divider}`,
  },
  attachmentImage: {
    width: "100%",
    height: 92,
    display: "block",
    objectFit: "cover",
    backgroundColor: theme.palette.action.hover,
  },
  attachmentName: {
    display: "block",
    padding: theme.spacing(0.75, 1),
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    color: theme.palette.text.secondary,
    fontSize: 12,
  },
  attachmentLoading: {
    height: 92,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: theme.palette.action.hover,
  },
  reply: {
    padding: theme.spacing(1.5, 2),
    borderTop: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
  },
  replyActions: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
    flexWrap: "wrap",
    [theme.breakpoints.down("xs")]: {
      display: "grid",
      gridTemplateColumns: "auto minmax(0, 1fr)",
      alignItems: "center",
    },
  },
  replyStatus: {
    minWidth: 170,
    [theme.breakpoints.down("xs")]: {
      width: "100%",
      minWidth: 0,
      gridColumn: "1 / -1",
    },
  },
  replySpacer: {
    flex: 1,
    [theme.breakpoints.down("xs")]: {
      display: "none",
    },
  },
  replySubmit: {
    [theme.breakpoints.down("xs")]: {
      justifySelf: "end",
    },
  },
  companyFilterControl: {
    minWidth: 0,
  },
  companyFilterMeta: {
    minHeight: 30,
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    paddingTop: theme.spacing(0.5),
  },
  companyFilterMessage: {
    color: theme.palette.text.secondary,
    fontSize: 12,
    flex: 1,
  },
  companyFilterError: {
    color: theme.palette.error.dark || theme.palette.error.main,
  },
  replyNotice: {
    marginBottom: theme.spacing(1),
    color: theme.palette.text.secondary,
    fontSize: 13,
    lineHeight: 1.45,
  },
  footerBadge: {
    backgroundColor: "#f44336 !important",
    color: "white",
    fontWeight: 700,
    boxShadow: theme.palette.type === "dark"
      ? "0 2px 4px rgba(0, 0, 0, 0.5)"
      : "0 2px 4px rgba(0, 0, 0, 0.1)",
    borderRadius: 8,
    fontSize: "0.6rem",
    height: 15,
    minWidth: 15,
    padding: "0 0px",
    top: "2px",
    right: "-4px",
  },
  closedNotice: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    color: theme.palette.text.secondary,
  },
  footerButton: {
    minWidth: 0,
    borderRadius: 6,
    padding: theme.spacing(0.5, 0.75),
    color: "inherit",
    display: "inline-flex",
    alignItems: "center",
    gap: theme.spacing(0.75),
    fontSize: 12,
    fontWeight: 600,
    lineHeight: 1.35,
    "&:hover": {
      color: theme.palette.primary.main,
      backgroundColor: theme.palette.action.hover,
    },
    "&:focus-visible": {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: 2,
    },
  },
  footerButtonCollapsed: {
    width: 44,
    height: 44,
    padding: 0,
    justifyContent: "center",
  },
  footerIcon: {
    fontSize: 18,
  },
  imageDialogPaper: {
    backgroundColor: "transparent",
    boxShadow: "none",
    overflow: "visible",
  },
  imageDialog: {
    position: "relative",
  },
  imageDialogImage: {
    display: "block",
    maxWidth: "90vw",
    maxHeight: "86vh",
    objectFit: "contain",
    borderRadius: 10,
  },
  imageDialogClose: {
    position: "absolute",
    top: -16,
    right: -16,
    color: "#fff",
    backgroundColor: "rgba(20,20,20,0.82)",
    "&:hover": {
      backgroundColor: "rgba(20,20,20,0.96)",
    },
  },
}));

const formatDateTime = (value) => {
  if (!value) return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";

  return new Intl.DateTimeFormat("pt-BR", {
    dateStyle: "short",
    timeStyle: "short",
  }).format(parsed);
};

const createPreviewItems = (files) => files.map((file) => ({
  file,
  url: window.URL.createObjectURL(file),
}));

const revokePreviewItems = (items) => {
  items.forEach((item) => {
    if (item?.url) window.URL.revokeObjectURL(item.url);
  });
};

const validateImages = (files, currentCount = 0) => {
  if (currentCount + files.length > MAX_IMAGES) {
    return `Envie no máximo ${MAX_IMAGES} imagens por vez.`;
  }

  const invalidType = files.find((file) => !ACCEPTED_IMAGE_TYPES.includes(file.type));
  if (invalidType) return "Use imagens JPEG, PNG ou WebP.";

  const oversized = files.find((file) => file.size > MAX_IMAGE_BYTES);
  if (oversized) return "Cada imagem pode ter no máximo 8 MiB.";

  return null;
};

const STATUS_CHIP_COLORS = {
  light: {
    open: { color: "#0B5CAD", backgroundColor: "#EAF4FF", borderColor: "#8ABBE8" },
    in_review: { color: "#8A3000", backgroundColor: "#FFF0E8", borderColor: "#E5A27C" },
    awaiting_user: { color: "#754600", backgroundColor: "#FFF4D6", borderColor: "#D8AE55" },
    resolved: { color: "#176B35", backgroundColor: "#E8F5EC", borderColor: "#83BE96" },
    closed: { color: "#46505C", backgroundColor: "#F1F3F5", borderColor: "#AAB1B9" },
  },
  dark: {
    open: { color: "#A9D2FF", backgroundColor: "#16344D", borderColor: "#4E789B" },
    in_review: { color: "#FFC1A8", backgroundColor: "#4A2518", borderColor: "#945B45" },
    awaiting_user: { color: "#FFD58A", backgroundColor: "#463510", borderColor: "#8E7131" },
    resolved: { color: "#A8E3BA", backgroundColor: "#193B27", borderColor: "#4F825F" },
    closed: { color: "#D8DDE3", backgroundColor: "#2B2F34", borderColor: "#69717A" },
  },
};

export const getSupportStatusColors = (theme, status) => {
  const mode = theme.palette.type === "dark" || theme.palette.mode === "dark" ? "dark" : "light";
  return STATUS_CHIP_COLORS[mode][status] || STATUS_CHIP_COLORS[mode].open;
};

const StatusChip = ({ status }) => {
  const theme = useTheme();
  const config = SUPPORT_STATUS[status] || SUPPORT_STATUS.open;
  const colors = getSupportStatusColors(theme, status);

  return (
    <Chip
      size="small"
      label={config.label}
      style={{
        ...colors,
        fontWeight: 600,
      }}
      variant="outlined"
    />
  );
};

const SupportAttachment = ({ attachment, onOpen }) => {
  const classes = useStyles();
  const rootRef = useRef(null);
  const [shouldLoad, setShouldLoad] = useState(false);
  const [url, setUrl] = useState("");
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (typeof IntersectionObserver !== "function" || !rootRef.current) {
      setShouldLoad(true);
      return undefined;
    }

    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        setShouldLoad(true);
        observer.disconnect();
      }
    }, { rootMargin: "160px" });
    observer.observe(rootRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!shouldLoad) return undefined;
    let active = true;
    let objectUrl = "";

    getSupportAttachment(attachment.id)
      .then(({ blob, contentType }) => {
        if (!active) return;
        const normalizedBlob = blob instanceof Blob
          ? blob
          : new Blob([blob], { type: contentType || attachment.mimeType });
        objectUrl = window.URL.createObjectURL(normalizedBlob);
        setUrl(objectUrl);
      })
      .catch(() => {
        if (active) setFailed(true);
      });

    return () => {
      active = false;
      if (objectUrl) window.URL.revokeObjectURL(objectUrl);
    };
  }, [attachment.id, attachment.mimeType, shouldLoad]);

  if (failed) {
    return (
      <Box className={classes.attachmentButton} p={1.25}>
        <Typography variant="caption" color="textSecondary">
          Não foi possível carregar {attachment.name}.
        </Typography>
      </Box>
    );
  }

  return (
    <ButtonBase
      ref={rootRef}
      className={classes.attachmentButton}
      onClick={() => url && onOpen({ url, name: attachment.name })}
      disabled={!url}
      aria-label={`Abrir imagem ${attachment.name}`}
    >
      {url ? (
        <img className={classes.attachmentImage} src={url} alt={attachment.name} loading="lazy" />
      ) : (
        <Box className={classes.attachmentLoading}>
          <CircularProgress size={22} />
        </Box>
      )}
      <span className={classes.attachmentName}>{attachment.name}</span>
    </ButtonBase>
  );
};

const EmptyState = ({ icon: Icon = ForumOutlinedIcon, title, description, action }) => {
  const classes = useStyles();
  return (
    <Box className={classes.statePane} role="status">
      <Icon className={classes.stateIcon} />
      <Typography variant="subtitle1">{title}</Typography>
      <Typography variant="body2" className={classes.secondaryText}>
        {description}
      </Typography>
      {action ? <Box mt={2}>{action}</Box> : null}
    </Box>
  );
};

const createEmptyReplyDraft = () => ({
  reply: "",
  replyStatus: "",
  replyImages: [],
  replyError: "",
});

const SupportIssuesExperience = ({ open, onClose, user, support }) => {
  const classes = useStyles();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const isSuper = Boolean(user?.super);
  const identityKey = user?.companyId && user?.id
    ? `supportIssue:draft:${user.companyId}:${user.id}`
    : "";
  const [activeView, setActiveView] = useState(isSuper ? "more" : "new");
  const [draft, setDraft] = useState({ subject: "", body: "" });
  const [draftReadyKey, setDraftReadyKey] = useState("");
  const [createImages, setCreateImages] = useState([]);
  const createImagesRef = useRef([]);
  const [createError, setCreateError] = useState("");
  const [creating, setCreating] = useState(false);
  const createInputRef = useRef(null);
  const createSubmitRef = useRef(null);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [companyFilter, setCompanyFilter] = useState("");
  const replyDraftsRef = useRef(new Map());
  const [, setReplyDraftRevision] = useState(0);
  const [replyingScopes, setReplyingScopes] = useState([]);
  const [statusChangingScopes, setStatusChangingScopes] = useState([]);
  const replyInputRef = useRef(null);
  const replySubmitRef = useRef(new Map());
  const statusSubmitRef = useRef(new Map());
  const [lightbox, setLightbox] = useState(null);
  const historyRef = useRef(null);
  const readFrontierRef = useRef(new Map());
  const uiIdentityRef = useRef(identityKey);
  const uiGenerationRef = useRef(0);
  const activeViewRef = useRef(activeView);
  activeViewRef.current = activeView;
  const replyScopeKey = identityKey && typeof activeView === "number"
    ? `${identityKey}:issue:${activeView}`
    : "";
  const activeReplyDraft = replyScopeKey
    ? replyDraftsRef.current.get(replyScopeKey) || createEmptyReplyDraft()
    : createEmptyReplyDraft();
  const {
    reply,
    replyStatus,
    replyImages,
    replyError,
  } = activeReplyDraft;
  const replying = Boolean(replyScopeKey && replyingScopes.includes(replyScopeKey));
  const statusChanging = Boolean(replyScopeKey && statusChangingScopes.includes(replyScopeKey));
  const draftMatchesIdentity = draftReadyKey === identityKey;
  const visibleDraft = draftMatchesIdentity ? draft : { subject: "", body: "" };
  const visibleCreateImages = draftMatchesIdentity ? createImages : [];
  const visibleCreateError = draftMatchesIdentity ? createError : "";

  const uiIdentityChanged = uiIdentityRef.current !== identityKey;
  if (uiIdentityChanged) {
    uiIdentityRef.current = identityKey;
    uiGenerationRef.current += 1;
    createSubmitRef.current = null;
    replySubmitRef.current.clear();
    statusSubmitRef.current.clear();
  }

  const closeSupportIssue = support.closeIssue;
  const loadSupportIssues = support.loadIssues;
  const markSupportRead = support.markRead;
  const selectedSupportIssue = support.selectedIssue;
  const supportMessages = support.messages;
  const loadSupportCompanies = support.loadCompanies;
  const replyPermissionKnown = typeof selectedSupportIssue?.canReply === "boolean";
  const canReply = Number(selectedSupportIssue?.id) === Number(activeView) &&
    selectedSupportIssue?.status !== "closed" &&
    (replyPermissionKnown ? selectedSupportIssue.canReply : isSuper);
  const replyBlockedMessage = replyPermissionKnown
    ? "Aguarde a resposta da equipe para enviar uma nova mensagem. Seu rascunho continua aqui."
    : "Atualize o chamado para verificar se já pode responder. Seu rascunho continua aqui.";

  const updateReplyDraft = (scopeKey, update) => {
    if (!scopeKey) return;
    const current = replyDraftsRef.current.get(scopeKey) || createEmptyReplyDraft();
    const next = typeof update === "function" ? update(current) : { ...current, ...update };
    replyDraftsRef.current.set(scopeKey, next);
    setReplyDraftRevision((revision) => revision + 1);
  };

  const clearReplyDraft = (scopeKey) => {
    if (!scopeKey) return;
    const current = replyDraftsRef.current.get(scopeKey);
    revokePreviewItems(current?.replyImages || []);
    replyDraftsRef.current.delete(scopeKey);
    setReplyDraftRevision((revision) => revision + 1);
  };

  useEffect(() => {
    createImagesRef.current = createImages;
  }, [createImages]);

  useEffect(() => () => {
    revokePreviewItems(createImagesRef.current);
    replyDraftsRef.current.forEach((entry) => revokePreviewItems(entry.replyImages || []));
    replyDraftsRef.current.clear();
  }, []);

  useEffect(() => {
    revokePreviewItems(createImagesRef.current);
    replyDraftsRef.current.forEach((entry) => revokePreviewItems(entry.replyImages || []));
    replyDraftsRef.current.clear();
    setCreateImages([]);
    setReplyDraftRevision((revision) => revision + 1);
    setCreateError("");
    setCreating(false);
    setReplyingScopes([]);
    setStatusChangingScopes([]);
    setLightbox(null);
    setSearch("");
    setStatusFilter("");
    setCompanyFilter("");
    readFrontierRef.current.clear();
    closeSupportIssue();

    let storedDraft = { subject: "", body: "" };
    if (identityKey) {
      try {
        storedDraft = JSON.parse(window.localStorage.getItem(identityKey)) || storedDraft;
      } catch (_error) {
        window.localStorage.removeItem(identityKey);
      }
    }
    setDraft({
      subject: String(storedDraft.subject || "").slice(0, 160),
      body: String(storedDraft.body || "").slice(0, 10000),
    });
    setDraftReadyKey(identityKey);
  }, [closeSupportIssue, identityKey]);

  useEffect(() => {
    if (!identityKey || draftReadyKey !== identityKey) return;
    if (!draft.subject && !draft.body) {
      window.localStorage.removeItem(identityKey);
      return;
    }
    window.localStorage.setItem(identityKey, JSON.stringify(draft));
  }, [draft, draftReadyKey, identityKey]);

  useEffect(() => {
    if (!open) return;
    setActiveView(isSuper ? "more" : "new");
    loadSupportIssues({ filters: { limit: 20 } }).catch(() => undefined);
  }, [isSuper, loadSupportIssues, open]);

  useEffect(() => {
    if (!open || !isSuper || typeof loadSupportCompanies !== "function") return;
    loadSupportCompanies({ limit: 20 }).catch(() => undefined);
  }, [isSuper, loadSupportCompanies, open]);

  useEffect(() => {
    if (!open) setLightbox(null);
  }, [open]);

  useEffect(() => {
    setLightbox(null);
  }, [identityKey, replyScopeKey]);

  useEffect(() => {
    const history = historyRef.current;
    const issueId = Number(selectedSupportIssue?.id);
    if (
      !open ||
      lightbox ||
      !history ||
      typeof activeView !== "number" ||
      !issueId ||
      Number(activeView) !== issueId ||
      supportMessages.length === 0
    ) return undefined;

    const readScopeKey = `${identityKey}:issue:${issueId}`;
    let frameId = null;
    let active = true;

    const measureVisibleMessages = () => {
      frameId = null;
      if (
        !active ||
        document.visibilityState !== "visible" ||
        uiIdentityRef.current !== identityKey ||
        Number(activeViewRef.current) !== issueId
      ) return;

      const viewport = history.getBoundingClientRect();
      if (viewport.bottom <= viewport.top) return;
      let highestVisibleId = 0;
      history.querySelectorAll("[data-support-message-id]").forEach((element) => {
        const bounds = element.getBoundingClientRect();
        const visible = bounds.bottom > viewport.top && bounds.top < viewport.bottom;
        if (visible) {
          highestVisibleId = Math.max(highestVisibleId, Number(element.dataset.supportMessageId) || 0);
        }
      });

      const confirmedReadId = Number(selectedSupportIssue.lastReadMessageId) || 0;
      const requestedReadId = Number(readFrontierRef.current.get(readScopeKey)) || 0;
      if (highestVisibleId <= Math.max(confirmedReadId, requestedReadId)) return;
      readFrontierRef.current.set(readScopeKey, highestVisibleId);
      markSupportRead(highestVisibleId).catch(() => {
        if (readFrontierRef.current.get(readScopeKey) === highestVisibleId) {
          readFrontierRef.current.set(readScopeKey, confirmedReadId);
        }
      });
    };

    const scheduleMeasure = () => {
      if (frameId !== null) window.cancelAnimationFrame(frameId);
      frameId = window.requestAnimationFrame(measureVisibleMessages);
    };
    const handleVisibility = () => {
      if (document.visibilityState === "visible") scheduleMeasure();
    };

    scheduleMeasure();
    history.addEventListener("scroll", scheduleMeasure, { passive: true });
    window.addEventListener("resize", scheduleMeasure);
    document.addEventListener("visibilitychange", handleVisibility);
    return () => {
      active = false;
      if (frameId !== null) window.cancelAnimationFrame(frameId);
      history.removeEventListener("scroll", scheduleMeasure);
      window.removeEventListener("resize", scheduleMeasure);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [activeView, identityKey, lightbox, markSupportRead, open, selectedSupportIssue, support.detailState.status, supportMessages]);

  const navIssues = useMemo(() => {
    const recent = support.issues.slice(0, 7);
    if (
      support.selectedIssue &&
      !recent.some((issue) => issue.id === support.selectedIssue.id)
    ) {
      return [support.selectedIssue, ...recent].slice(0, 8);
    }
    return recent;
  }, [support.issues, support.selectedIssue]);

  const addImages = (event, target) => {
    const files = Array.from(event.target.files || []);
    event.target.value = "";
    if (target === "reply" && !canReply) return;
    const current = target === "create" ? visibleCreateImages : replyImages;
    const error = validateImages(files, current.length);
    if (error) {
      if (target === "create") setCreateError(error);
      else updateReplyDraft(replyScopeKey, { replyError: error });
      return;
    }
    const previews = createPreviewItems(files);
    if (target === "create") {
      setCreateImages((items) => [...items, ...previews]);
      setCreateError("");
    } else {
      updateReplyDraft(replyScopeKey, (currentDraft) => ({
        ...currentDraft,
        replyImages: [...currentDraft.replyImages, ...previews],
        replyError: "",
      }));
    }
  };

  const removeImage = (index, target) => {
    if (target === "create") {
      setCreateImages((items) => {
        const removed = items[index];
        if (removed?.url) window.URL.revokeObjectURL(removed.url);
        return items.filter((_, itemIndex) => itemIndex !== index);
      });
      return;
    }

    updateReplyDraft(replyScopeKey, (currentDraft) => {
      const removed = currentDraft.replyImages[index];
      if (removed?.url) window.URL.revokeObjectURL(removed.url);
      return {
        ...currentDraft,
        replyImages: currentDraft.replyImages.filter((_, itemIndex) => itemIndex !== index),
      };
    });
  };

  const clearCreateImages = () => {
    revokePreviewItems(createImagesRef.current);
    setCreateImages([]);
  };

  const handleCreate = async (event) => {
    event.preventDefault();
    if (createSubmitRef.current) return;

    const subject = visibleDraft.subject.trim();
    const body = visibleDraft.body.trim();
    if (!subject || !body) {
      setCreateError("Informe o assunto e descreva o que aconteceu.");
      return;
    }

    const token = { generation: uiGenerationRef.current, identityKey };
    createSubmitRef.current = token;
    setCreating(true);
    setCreateError("");
    try {
      const data = await support.submitIssue({
        subject,
        body,
        images: visibleCreateImages.map((item) => item.file),
      });
      if (data) window.localStorage.removeItem(token.identityKey);
      if (
        !data ||
        createSubmitRef.current !== token ||
        token.generation !== uiGenerationRef.current ||
        token.identityKey !== uiIdentityRef.current
      ) return;
      setDraft({ subject: "", body: "" });
      clearCreateImages();
      setActiveView(Number(data.issue?.id));
      toast.success("Chamado criado. Você pode acompanhar as respostas aqui.");
    } catch (error) {
      if (
        createSubmitRef.current !== token ||
        token.generation !== uiGenerationRef.current ||
        token.identityKey !== uiIdentityRef.current
      ) return;
      setCreateError(isAmbiguousSupportRequestError(error)
        ? "Não foi possível confirmar se o chamado foi criado. Atualize a lista antes de tentar novamente."
        : getSupportErrorMessage(error, "Não foi possível criar o chamado. Tente novamente."));
    } finally {
      if (createSubmitRef.current === token) {
        createSubmitRef.current = null;
        setCreating(false);
      }
    }
  };

  const applyFilters = ({
    searchValue = search,
    statusValue = statusFilter,
    companyValue = companyFilter,
  } = {}) => {
    support.loadIssues({
      filters: {
        limit: 20,
        ...(searchValue.trim() ? { search: searchValue.trim() } : {}),
        ...(statusValue ? { status: statusValue } : {}),
        ...(isSuper && companyValue ? { companyId: companyValue } : {}),
      },
    }).catch(() => undefined);
  };

  const handleFilter = (event) => {
    event.preventDefault();
    applyFilters();
  };

  const handleReply = async (event) => {
    event.preventDefault();
    if (!canReply || !replyScopeKey || replySubmitRef.current.has(replyScopeKey)) return;
    if (!reply.trim() && replyImages.length === 0 && !replyStatus) {
      updateReplyDraft(replyScopeKey, { replyError: "Escreva uma resposta ou adicione uma imagem." });
      return;
    }
    if (!reply.trim() && replyImages.length === 0 && replyStatus) {
      await handleStatusChange(replyStatus, { clearDraftStatus: true });
      return;
    }

    const token = {
      generation: uiGenerationRef.current,
      identityKey,
      issueId: activeView,
      replyScopeKey,
    };
    const submittedDraft = replyDraftsRef.current.get(replyScopeKey) || createEmptyReplyDraft();
    replySubmitRef.current.set(replyScopeKey, token);
    setReplyingScopes((scopes) => [...new Set([...scopes, replyScopeKey])]);
    updateReplyDraft(replyScopeKey, { replyError: "" });
    try {
      const data = await support.submitMessage({
        body: submittedDraft.reply.trim(),
        status: isSuper ? submittedDraft.replyStatus || undefined : undefined,
        images: submittedDraft.replyImages.map((item) => item.file),
      });
      if (
        !data ||
        replySubmitRef.current.get(token.replyScopeKey) !== token ||
        token.generation !== uiGenerationRef.current ||
        token.identityKey !== uiIdentityRef.current
      ) return;
      clearReplyDraft(token.replyScopeKey);
    } catch (error) {
      if (
        replySubmitRef.current.get(token.replyScopeKey) !== token ||
        token.generation !== uiGenerationRef.current ||
        token.identityKey !== uiIdentityRef.current
      ) return;
      updateReplyDraft(token.replyScopeKey, {
        replyError: isAmbiguousSupportRequestError(error)
          ? "Não foi possível confirmar o envio. Atualize o chamado antes de enviar novamente."
          : getSupportErrorMessage(error, "Não foi possível enviar a resposta."),
      });
    } finally {
      if (replySubmitRef.current.get(token.replyScopeKey) === token) {
        replySubmitRef.current.delete(token.replyScopeKey);
        setReplyingScopes((scopes) => scopes.filter((scope) => scope !== token.replyScopeKey));
      }
    }
  };

  const handleStatusChange = async (status, { clearDraftStatus = false } = {}) => {
    if (!status || status === support.selectedIssue?.status) return;
    if (!replyScopeKey || statusSubmitRef.current.has(replyScopeKey)) return;
    const token = {
      generation: uiGenerationRef.current,
      identityKey,
      issueId: activeView,
      replyScopeKey,
    };
    statusSubmitRef.current.set(replyScopeKey, token);
    setStatusChangingScopes((scopes) => [...new Set([...scopes, replyScopeKey])]);
    updateReplyDraft(replyScopeKey, { replyError: "" });
    try {
      const data = await support.changeStatus(status);
      if (
        !data ||
        statusSubmitRef.current.get(token.replyScopeKey) !== token ||
        token.generation !== uiGenerationRef.current ||
        token.identityKey !== uiIdentityRef.current
      ) return;
      if (clearDraftStatus) {
        updateReplyDraft(token.replyScopeKey, (currentDraft) => ({
          ...currentDraft,
          replyStatus: "",
          replyError: "",
        }));
      }
    } catch (error) {
      if (
        statusSubmitRef.current.get(token.replyScopeKey) !== token ||
        token.generation !== uiGenerationRef.current ||
        token.identityKey !== uiIdentityRef.current
      ) return;
      updateReplyDraft(token.replyScopeKey, {
        replyError: getSupportErrorMessage(error, "Não foi possível alterar o status."),
      });
    } finally {
      if (statusSubmitRef.current.get(token.replyScopeKey) === token) {
        statusSubmitRef.current.delete(token.replyScopeKey);
        setStatusChangingScopes((scopes) => scopes.filter((scope) => scope !== token.replyScopeKey));
      }
    }
  };

  const renderPreviewGrid = (items, target) => items.length > 0 ? (
    <Box className={classes.previewGrid}>
      {items.map((item, index) => (
        <Box className={classes.previewItem} key={`${item.file.name}-${item.file.lastModified}-${index}`}>
          <img className={classes.previewImage} src={item.url} alt={`Prévia de ${item.file.name}`} />
          <Tooltip title="Remover imagem">
            <IconButton
              className={classes.removePreview}
              size="small"
              onClick={() => removeImage(index, target)}
              aria-label={`Remover ${item.file.name}`}
              disabled={target === "create" ? creating : replying}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <span className={classes.previewName}>{item.file.name}</span>
        </Box>
      ))}
    </Box>
  ) : null;

  const renderCreate = () => (
    <Box className={classes.createPane}>
      <form className={classes.createForm} onSubmit={handleCreate} noValidate>
        <Box className={classes.intro}>
          <Typography className={classes.introTitle}>Conte o que podemos melhorar</Typography>
          <Typography variant="body2" className={classes.secondaryText}>
            Descreva um bug ou sugestão com detalhes. Imagens ajudam a equipe a entender o cenário.
          </Typography>
        </Box>
        <TextField
          id="support-issue-subject"
          className={classes.field}
          label="Assunto"
          variant="outlined"
          fullWidth
          required
          value={visibleDraft.subject}
          onChange={(event) => setDraft((current) => ({
            ...current,
            subject: event.target.value.slice(0, 160),
          }))}
          inputProps={{ maxLength: 160 }}
          disabled={creating || !draftMatchesIdentity}
        />
        <TextField
          id="support-issue-description"
          className={`${classes.field} ${classes.createDescription}`}
          label="Descrição"
          variant="outlined"
          fullWidth
          required
          multiline
          rows={6}
          value={visibleDraft.body}
          onChange={(event) => setDraft((current) => ({
            ...current,
            body: event.target.value.slice(0, 10000),
          }))}
          inputProps={{ maxLength: 10000 }}
          disabled={creating || !draftMatchesIdentity}
          helperText="Inclua o que você esperava e o que aconteceu."
        />
        <Typography className={classes.counter}>{visibleDraft.body.length}/10.000</Typography>
        <Box className={classes.uploadRow}>
          <input
            hidden
            ref={createInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            multiple
            onChange={(event) => addImages(event, "create")}
          />
          <Button
            className={classes.uploadButton}
            variant="outlined"
            color="primary"
            startIcon={<AddPhotoAlternateOutlinedIcon />}
            onClick={() => createInputRef.current?.click()}
            disabled={creating || !draftMatchesIdentity || visibleCreateImages.length >= MAX_IMAGES}
          >
            Adicionar imagens
          </Button>
          <Typography variant="caption" className={classes.secondaryText}>
            JPEG, PNG ou WebP · até 5 arquivos de 8 MiB
          </Typography>
        </Box>
        {renderPreviewGrid(visibleCreateImages, "create")}
        {visibleCreateError ? <Box className={classes.errorBox} role="alert">{visibleCreateError}</Box> : null}
        <Box className={classes.formActions}>
          <ButtonWithSpinner
            className={classes.primaryButton}
            type="submit"
            variant="contained"
            color="primary"
            loading={creating}
            disabled={!draftMatchesIdentity || !visibleDraft.subject.trim() || !visibleDraft.body.trim()}
            startIcon={<SendOutlinedIcon />}
          >
            Enviar chamado
          </ButtonWithSpinner>
        </Box>
      </form>
    </Box>
  );

  const renderIssueList = () => {
    if (support.listState.status === "loading" && support.issues.length === 0) {
      return <EmptyState title="Carregando chamados" description="Buscando as conversas mais recentes." />;
    }
    if (support.listState.status === "error" && support.issues.length === 0) {
      return (
        <EmptyState
          title="Não foi possível carregar"
          description="Verifique sua conexão e tente novamente."
          action={(
            <Button startIcon={<RefreshIcon />} onClick={handleFilter} color="primary">
              Tentar novamente
            </Button>
          )}
        />
      );
    }
    if (support.issues.length === 0) {
      return (
        <EmptyState
          title="Nenhum chamado encontrado"
          description={search || statusFilter || companyFilter
            ? "Ajuste os filtros para ampliar a busca."
            : "Seus novos bugs e sugestões aparecerão aqui."}
        />
      );
    }

    return (
      <Box className={classes.listScroll}>
        {support.listState.status === "loading" ? (
          <Box className={classes.listFeedback} role="status">
            <CircularProgress size={16} />
            <span>Atualizando chamados. Exibindo os resultados anteriores.</span>
          </Box>
        ) : null}
        {support.listState.status === "error" ? (
          <Box className={`${classes.listFeedback} ${classes.listFeedbackError}`} role="alert">
            <span>Não foi possível atualizar. Exibindo os resultados anteriores.</span>
            <Button
              className={classes.listFeedbackAction}
              size="small"
              color="inherit"
              startIcon={<RefreshIcon />}
              onClick={handleFilter}
            >
              Tentar novamente
            </Button>
          </Box>
        ) : null}
        {support.issues.map((issue) => (
          <ButtonBase
            key={issue.id}
            className={`${classes.issueRow} ${support.selectedIssue?.id === issue.id ? classes.issueRowSelected : ""}`}
            onClick={() => {
              setActiveView(Number(issue.id));
              support.openIssue(issue.id).catch(() => undefined);
            }}
            aria-current={Number(activeView) === Number(issue.id) ? "page" : undefined}
          >
            <Box className={classes.issueRowTop}>
              <span className={classes.issueCode}>{issue.code}</span>
              <StatusChip status={issue.status} isSuper={isSuper} />
              {issue.unread ? <span className={classes.unreadDot} role="status" aria-label="Não lido" /> : null}
            </Box>
            <Typography className={classes.issueSubject}>{issue.subject}</Typography>
            <Box className={classes.issueMeta}>
              <span>{isSuper ? issue.company?.name : issue.author?.name}</span>
              <span>{formatDateTime(issue.lastActivityAt || issue.updatedAt)}</span>
            </Box>
          </ButtonBase>
        ))}
        {support.issuesCursor ? (
          <ButtonWithSpinner
            className={classes.loadMore}
            variant="outlined"
            loading={support.listState.status === "loading-more"}
            onClick={() => support.loadIssues({
              append: true,
              cursor: support.issuesCursor,
            }).catch(() => undefined)}
          >
            Carregar mais
          </ButtonWithSpinner>
        ) : null}
      </Box>
    );
  };

  const renderHistory = () => {
    if (support.detailState.status === "loading") {
      return <EmptyState title="Abrindo chamado" description="Carregando o histórico completo." />;
    }
    if (support.detailState.status === "error") {
      return (
        <EmptyState
          title="Não foi possível abrir"
          description="Atualize o chamado para tentar novamente."
          action={(
            <Button
              startIcon={<RefreshIcon />}
              onClick={() => support.openIssue(support.selectedIssue?.id).catch(() => undefined)}
            >
              Atualizar
            </Button>
          )}
        />
      );
    }

    return (
      <Box ref={historyRef} className={classes.history} aria-live="polite" data-support-history>
        {support.messagesCursor ? (
          <Box textAlign="center" mb={1}>
            <ButtonWithSpinner
              variant="text"
              loading={support.detailState.status === "loading-more"}
              onClick={() => support.loadOlderMessages().catch(() => undefined)}
            >
              Ver mensagens anteriores
            </ButtonWithSpinner>
          </Box>
        ) : null}
        {support.messages.map((message, index) => (
          <React.Fragment key={message.id}>
            {index > 0 ? <Divider /> : null}
            <Box className={classes.message} data-support-message-id={message.id}>
              {message.kind === "status" ? (
                <Box className={classes.statusActivity}>
                  <span>{message.actor?.name || "Sistema"}</span>
                  <span>alterou o status</span>
                  {message.previousStatus ? <StatusChip status={message.previousStatus} isSuper={isSuper} /> : null}
                  <span>para</span>
                  <StatusChip status={message.status} isSuper={isSuper} />
                  <span>{formatDateTime(message.createdAt)}</span>
                </Box>
              ) : (
                <>
                  <Box className={classes.messageHeader}>
                    <span className={classes.actor}>{message.actor?.name || "Usuário"}</span>
                    <span className={classes.timestamp}>{formatDateTime(message.createdAt)}</span>
                  </Box>
                  {message.body ? <Typography className={classes.messageBody}>{message.body}</Typography> : null}
                </>
              )}
              {Array.isArray(message.attachments) && message.attachments.length > 0 ? (
                <Box className={classes.attachments}>
                  {message.attachments.map((attachment) => (
                    <SupportAttachment
                      key={attachment.id}
                      attachment={attachment}
                      onOpen={setLightbox}
                    />
                  ))}
                </Box>
              ) : null}
            </Box>
          </React.Fragment>
        ))}
      </Box>
    );
  };

  const renderReply = () => {
    if (support.selectedIssue?.status === "closed") {
      return (
        <Box className={classes.reply}>
          <Box className={classes.closedNotice}>
            <Typography variant="body2">
              Este chamado está finalizado. {isSuper ? "Reabra para continuar a conversa." : "A equipe precisa reabri-lo antes de uma nova resposta."}
            </Typography>
            {isSuper ? (
              <ButtonWithSpinner
                variant="outlined"
                color="primary"
                loading={statusChanging}
                onClick={() => handleStatusChange("open")}
              >
                Reabrir chamado
              </ButtonWithSpinner>
            ) : null}
          </Box>
          {replyError ? <Box className={classes.errorBox} role="alert">{replyError}</Box> : null}
        </Box>
      );
    }

    return (
      <Box className={classes.reply} component="form" onSubmit={handleReply}>
        {!canReply ? (
          <Box id="support-reply-availability" className={classes.replyNotice} role="status">
            {replyBlockedMessage}
            {!replyPermissionKnown ? (
              <Button
                size="small"
                color="primary"
                onClick={() => support.openIssue(activeView, { silent: true }).catch(() => undefined)}
              >
                Atualizar chamado
              </Button>
            ) : null}
          </Box>
        ) : null}
        <TextField
          id="support-reply-body"
          label="Responder"
          placeholder="Escreva uma atualização para este chamado"
          variant="outlined"
          size="small"
          fullWidth
          multiline
          minRows={3}
          maxRows={6}
          value={reply}
          onChange={(event) => updateReplyDraft(replyScopeKey, {
            reply: event.target.value.slice(0, 10000),
          })}
          disabled={replying}
          inputProps={{
            maxLength: 10000,
            "aria-describedby": !canReply ? "support-reply-availability" : undefined,
          }}
        />
        {renderPreviewGrid(replyImages, "reply")}
        {replyError ? <Box className={classes.errorBox} role="alert">{replyError}</Box> : null}
        <Box className={classes.replyActions}>
          <input
            hidden
            ref={replyInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            multiple
            onChange={(event) => addImages(event, "reply")}
            disabled={replying || !canReply}
          />
          <Tooltip title="Adicionar imagens">
            <span>
              <IconButton
                color="primary"
                onClick={() => replyInputRef.current?.click()}
                disabled={replying || !canReply || replyImages.length >= MAX_IMAGES}
                aria-label="Adicionar imagens à resposta"
              >
                <ImageOutlinedIcon />
              </IconButton>
            </span>
          </Tooltip>
          {isSuper ? (
            <FormControl className={classes.replyStatus} variant="outlined" size="small">
              <InputLabel id="support-reply-status-label" shrink>Status após enviar</InputLabel>
              <Select
                id="support-reply-status"
                labelId="support-reply-status-label"
                value={replyStatus}
                onChange={(event) => updateReplyDraft(replyScopeKey, {
                  replyStatus: event.target.value,
                })}
                label="Status após enviar"
                notched
                displayEmpty
                renderValue={(value) => value
                  ? SUPPORT_STATUS[value]?.label || value
                  : "Manter atual"}
                disabled={replying}
                inputProps={{ "aria-label": "Status após enviar" }}
              >
                <MenuItem value="">Manter atual</MenuItem>
                {Object.entries(SUPPORT_STATUS).map(([value, config]) => (
                  <MenuItem
                    key={value}
                    value={value}
                    disabled={value === support.selectedIssue?.status}
                  >
                    {config.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : null}
          <span className={classes.replySpacer} />
          <ButtonWithSpinner
            className={`${classes.primaryButton} ${classes.replySubmit}`}
            type="submit"
            variant="contained"
            color="primary"
            loading={replying}
            disabled={
              !canReply || (
                !reply.trim() &&
                replyImages.length === 0 &&
                (!replyStatus || replyStatus === support.selectedIssue?.status)
              )
            }
            startIcon={<SendOutlinedIcon />}
          >
            Responder
          </ButtonWithSpinner>
        </Box>
      </Box>
    );
  };

  const renderBrowser = () => (
    <Box className={classes.browserPane}>
      <Box className={classes.listPane}>
        <Box component="form" className={`${classes.filters} ${isSuper ? classes.filtersSuper : ""}`} onSubmit={handleFilter}>
          <Button
            className={classes.newIssueButton}
            color="primary"
            variant="contained"
            onClick={() => {
              support.closeIssue();
              setActiveView("new");
            }}
          >
            Novo chamado
          </Button>
          <TextField
            variant="outlined"
            size="small"
            className={classes.searchField}
            placeholder="Buscar por assunto ou código"
            inputProps={{ "aria-label": "Buscar chamados" }}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") handleFilter(event);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
              ),
            }}
          />
          <Box className={classes.filterFields}>
            <FormControl variant="outlined" size="small">
              <InputLabel id="support-status-filter-label">Status</InputLabel>
              <Select
                labelId="support-status-filter-label"
                label="Status"
                value={statusFilter}
                onChange={(event) => {
                  setStatusFilter(event.target.value);
                  applyFilters({ statusValue: event.target.value });
                }}
              >
                <MenuItem value="">Todos</MenuItem>
                {Object.entries(SUPPORT_STATUS).map(([value, config]) => (
                  <MenuItem key={value} value={value}>{config.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            {isSuper ? (
              <Box className={classes.companyFilterControl}>
                <FormControl variant="outlined" size="small" fullWidth>
                  <InputLabel id="support-company-filter-label">Empresa</InputLabel>
                  <Select
                    labelId="support-company-filter-label"
                    label="Empresa"
                    value={companyFilter}
                    onChange={(event) => {
                      setCompanyFilter(event.target.value);
                      applyFilters({ companyValue: event.target.value });
                    }}
                    disabled={support.companiesState?.status === "loading" && support.companies.length === 0}
                  >
                    <MenuItem value="">Todas</MenuItem>
                    {support.companies.map((company) => (
                      <MenuItem key={company.id} value={company.id}>{company.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {support.companiesCursor || ["loading", "error"].includes(support.companiesState?.status) ? (
                <Box className={classes.companyFilterMeta}>
                  {support.companiesState?.status === "loading" ? (
                    <span className={classes.companyFilterMessage}>Carregando empresas…</span>
                  ) : null}
                  {support.companiesState?.status === "error" ? (
                    <>
                      <span className={`${classes.companyFilterMessage} ${classes.companyFilterError}`}>
                        Não foi possível carregar as empresas.
                      </span>
                      <Button
                        size="small"
                        color="primary"
                        onClick={() => loadSupportCompanies({ limit: 20 }).catch(() => undefined)}
                      >
                        Tentar novamente
                      </Button>
                    </>
                  ) : null}
                  {support.companiesCursor ? (
                    <ButtonWithSpinner
                      size="small"
                      color="primary"
                      loading={support.companiesState?.status === "loading-more"}
                      onClick={() => loadSupportCompanies({
                        append: true,
                        cursor: support.companiesCursor,
                        limit: 20,
                      }).catch(() => undefined)}
                    >
                      Mais empresas
                    </ButtonWithSpinner>
                  ) : null}
                </Box>
                ) : null}
              </Box>
            ) : null}
          </Box>
        </Box>
        <Divider />
        {renderIssueList()}
      </Box>
    </Box>
  );

  const renderDetail = () => (
    <Box className={classes.detailPane}>
      {support.selectedIssue && Number(support.selectedIssue.id) === Number(activeView) ? (
        <>
          <Box className={classes.detailHeader}>
            <IconButton
              className={classes.mobileBack}
              onClick={() => {
                support.closeIssue();
                setActiveView("more");
              }}
              aria-label="Voltar para os chamados"
            >
              <ArrowBackIcon />
            </IconButton>
            <Box className={classes.detailHeading}>
              <Typography variant="caption" className={classes.issueCode}>
                {support.selectedIssue.code}
              </Typography>
              <Typography className={classes.detailTitle}>{support.selectedIssue.subject}</Typography>
            </Box>
            <StatusChip status={support.selectedIssue.status} isSuper={isSuper} />
          </Box>
          {renderHistory()}
          {renderReply()}
        </>
      ) : support.detailState.status === "error" ? (
        <EmptyState
          title="Não foi possível abrir"
          description="Atualize o chamado para tentar novamente."
          action={(
            <Button
              startIcon={<RefreshIcon />}
              onClick={() => support.openIssue(activeView).catch(() => undefined)}
            >
              Atualizar
            </Button>
          )}
        />
      ) : (
        <EmptyState title="Abrindo chamado" description="Carregando o histórico completo." />
      )}
    </Box>
  );

  return (
    <>
      <Dialog
        open={open}
        onClose={creating || replying ? undefined : onClose}
        maxWidth="lg"
        fullWidth
        fullScreen={isSmallScreen}
        classes={{ paper: classes.dialogPaper }}
        aria-labelledby="support-issues-title"
      >
        <Box className={classes.header}>
          <Box className={classes.headerIcon}><BugReportOutlinedIcon fontSize="small" /></Box>
          <Box className={classes.headerCopy}>
            <Typography id="support-issues-title" className={classes.headerTitle}>Suporte interno</Typography>
            <Typography variant="body2" className={classes.headerSubtitle}>
              Bugs, sugestões e respostas da equipe NeoZapp
            </Typography>
          </Box>
          <Button
            className={classes.closeButton}
            onClick={onClose}
            variant="outlined"
            disabled={creating || replying}
          >
            Fechar
          </Button>
        </Box>
        <Box className={classes.issueNav} aria-label="Chamados recentes">
          {navIssues.map((issue) => (
            <Button
              key={issue.id}
              className={Number(activeView) === Number(issue.id)
                ? classes.issueNavButtonSelected
                : classes.issueNavButton}
              variant={Number(activeView) === Number(issue.id) ? "contained" : "outlined"}
              aria-pressed={Number(activeView) === Number(issue.id)}
              aria-current={Number(activeView) === Number(issue.id) ? "page" : undefined}
              onClick={() => {
                setActiveView(Number(issue.id));
                support.openIssue(issue.id).catch(() => undefined);
              }}
            >
              {issue.code}
            </Button>
          ))}
          <Button
            className={activeView === "more"
              ? classes.issueNavButtonSelected
              : classes.issueNavButton}
            variant={activeView === "more" ? "contained" : "outlined"}
            aria-pressed={activeView === "more"}
            aria-current={activeView === "more" ? "page" : undefined}
            onClick={() => {
              support.closeIssue();
              setActiveView("more");
            }}
          >
            Mais chamados
          </Button>
        </Box>
        <Box className={classes.body}>
          {activeView === "new"
            ? renderCreate()
            : activeView === "more"
              ? renderBrowser()
              : renderDetail()}
        </Box>
      </Dialog>

      <Dialog
        open={Boolean(lightbox) && !uiIdentityChanged}
        onClose={() => setLightbox(null)}
        maxWidth={false}
        classes={{ paper: classes.imageDialogPaper }}
        aria-label={lightbox ? `Imagem ${lightbox.name}` : "Imagem do chamado"}
      >
        {lightbox && !uiIdentityChanged ? (
          <Box className={classes.imageDialog}>
            <img className={classes.imageDialogImage} src={lightbox.url} alt={lightbox.name} />
            <IconButton
              className={classes.imageDialogClose}
              onClick={() => setLightbox(null)}
              aria-label="Fechar imagem"
            >
              <CloseIcon />
            </IconButton>
          </Box>
        ) : null}
      </Dialog>
    </>
  );
};

export const SupportIssuesButton = ({ user, socketManager, collapsed = false, className }) => {
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const support = useSupportIssues({
    companyId: user?.companyId,
    userId: user?.id,
    socketManager,
  });
  const label = user?.super ? "Chamados" : "Reportar";

  return (
    <>
      <Tooltip title={collapsed ? label : "Abrir suporte interno"} placement="right">
        <ButtonBase
          className={`${className || classes.footerButton} ${collapsed ? classes.footerButtonCollapsed : ""}`}
          onClick={() => setOpen(true)}
          aria-label={`${label}${support.unreadCount ? `, ${support.unreadCount} não lidos` : ""}`}
        >
          {!collapsed ? <BugReportOutlinedIcon className={classes.footerIcon} /> : null}
          <Badge
            badgeContent={support.unreadCount}
            color="secondary"
            anchorOrigin={{ vertical: "top", horizontal: "right" }}
            classes={{ badge: classes.footerBadge }}
          >
            {collapsed
              ? <BugReportOutlinedIcon className={classes.footerIcon} />
              : <span>{label}</span>}
          </Badge>
        </ButtonBase>
      </Tooltip>
      <SupportIssuesExperience
        key={`${user?.companyId || ""}:${user?.id || ""}`}
        open={open}
        onClose={() => setOpen(false)}
        user={user}
        support={support}
      />
    </>
  );
};

const SupportIssuesModal = ({ open, onClose, user, socketManager }) => {
  const support = useSupportIssues({
    companyId: user?.companyId,
    userId: user?.id,
    socketManager,
  });

  return (
    <SupportIssuesExperience
      key={`${user?.companyId || ""}:${user?.id || ""}`}
      open={open}
      onClose={onClose}
      user={user}
      support={support}
    />
  );
};

export default SupportIssuesModal;
